import type {
  ListingPhotoCoachSettings,
  ListingPhotoGateMode,
} from "@/lib/listingPhotoCoachSettings";
import { withTimeout } from "@/lib/withTimeout";

export const LISTING_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const LISTING_IMAGE_ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
] as const;

/** Client-side decode + quality analysis budget (large phone photos). */
export const LISTING_IMAGE_VALIDATION_TIMEOUT_MS = 30_000;

/** WebP compression budget for files over the skip threshold. */
export const LISTING_IMAGE_COMPRESS_TIMEOUT_MS = 45_000;

/** Downscaled analysis canvas — blur/dark heuristics only. */
export const LISTING_IMAGE_ANALYSIS_MAX_DIMENSION = 512;

/** Laplacian variance below this suggests a very blurry capture (downscaled analysis). */
export const LISTING_IMAGE_BLUR_VARIANCE_THRESHOLD = 80;

/** Mean luminance (0–255) below this suggests the photo is too dark. */
export const LISTING_IMAGE_MIN_MEAN_BRIGHTNESS = 45;

export type ListingImageValidationIssue =
  | "too_large"
  | "unsupported_type"
  | "too_small"
  | "too_blurry"
  | "too_dark";

export type ListingImageValidationResult = {
  ok: boolean;
  blocked: boolean;
  warned: boolean;
  issues: ListingImageValidationIssue[];
  message?: string;
  shortEdge?: number;
  blurScore?: number;
  meanBrightness?: number;
};

export type ListingImageQualityMetrics = {
  blurScore: number;
  meanBrightness: number;
};

type DecodedListingImage = {
  width: number;
  height: number;
  analysisCanvas: HTMLCanvasElement | null;
};

/**
 * Single decode: natural dimensions + downscaled canvas for blur/brightness.
 * Avoids separate Image() + createImageBitmap passes (Phase 4 upload perf).
 */
async function decodeListingImageOnce(
  file: File,
): Promise<DecodedListingImage | null> {
  if (typeof document === "undefined") {
    return null;
  }

  try {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    });
    const width = bitmap.width;
    const height = bitmap.height;

    let analysisCanvas: HTMLCanvasElement | null = null;
    const maxDim = LISTING_IMAGE_ANALYSIS_MAX_DIMENSION;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const canvasWidth = Math.max(1, Math.round(width * scale));
    const canvasHeight = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.drawImage(bitmap, 0, 0, canvasWidth, canvasHeight);
      analysisCanvas = canvas;
    }

    bitmap.close();
    return { width, height, analysisCanvas };
  } catch {
    return null;
  }
}

export function computeBlurVariance(
  gray: Float32Array,
  width: number,
  height: number,
): number {
  let sum = 0;
  let sumSq = 0;
  let count = 0;

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const lap =
        -4 * gray[idx] +
        gray[idx - 1] +
        gray[idx + 1] +
        gray[idx - width] +
        gray[idx + width];
      sum += lap;
      sumSq += lap * lap;
      count++;
    }
  }

  if (count === 0) {
    return 0;
  }

  const mean = sum / count;
  return sumSq / count - mean * mean;
}

export function analyzeListingImageQualityFromCanvas(
  canvas: HTMLCanvasElement,
): ListingImageQualityMetrics {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return {
      blurScore: LISTING_IMAGE_BLUR_VARIANCE_THRESHOLD,
      meanBrightness: 128,
    };
  }

  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  const gray = new Float32Array(width * height);
  let brightnessSum = 0;

  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    gray[j] = lum;
    brightnessSum += lum;
  }

  return {
    blurScore: computeBlurVariance(gray, width, height),
    meanBrightness: brightnessSum / gray.length,
  };
}

export async function getListingImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  const decoded = await withTimeout(
    decodeListingImageOnce(file),
    LISTING_IMAGE_VALIDATION_TIMEOUT_MS,
    "Timed out while reading image dimensions.",
  );

  if (!decoded) {
    return { width: 0, height: 0 };
  }

  return { width: decoded.width, height: decoded.height };
}

/** @deprecated Prefer single-pass validation; kept for callers that only need quality. */
export async function analyzeListingImageQuality(
  file: File,
): Promise<ListingImageQualityMetrics> {
  const decoded = await decodeListingImageOnce(file);
  if (!decoded?.analysisCanvas) {
    return {
      blurScore: LISTING_IMAGE_BLUR_VARIANCE_THRESHOLD,
      meanBrightness: 128,
    };
  }
  return analyzeListingImageQualityFromCanvas(decoded.analysisCanvas);
}

const ISSUE_MESSAGES: Record<ListingImageValidationIssue, string> = {
  too_large: "This image is too large. Maximum size is 5MB.",
  unsupported_type: "Unsupported format. Please use JPEG, PNG, GIF, or WebP.",
  too_small:
    "This photo is too small. Use a higher-resolution image (shortest side at least {min}px).",
  too_blurry:
    "This photo looks blurry. Retake in good light and hold the phone steady until the image is sharp.",
  too_dark:
    "This photo looks too dark. Move to better lighting or turn on your flash so buyers can see the item clearly.",
};

function issueApplies(
  issue: ListingImageValidationIssue,
  settings: ListingPhotoCoachSettings,
): ListingPhotoGateMode | "always" {
  if (issue === "too_large" || issue === "unsupported_type") {
    return "always";
  }
  if (issue === "too_small") {
    return settings.listing_photo_min_dimension_mode;
  }
  return settings.listing_photo_quality_check_mode;
}

function buildMessage(
  issues: ListingImageValidationIssue[],
  fileName: string,
  minShortEdge: number,
): string {
  const primary = issues[0];
  const base =
    ISSUE_MESSAGES[primary]?.replace("{min}", String(minShortEdge)) ??
    "This image could not be uploaded.";
  return `${fileName}: ${base}`;
}

function finalizeValidationResult(
  issues: ListingImageValidationIssue[],
  settings: ListingPhotoCoachSettings,
  fileName: string,
  extras?: {
    shortEdge?: number;
    blurScore?: number;
    meanBrightness?: number;
  },
): ListingImageValidationResult {
  let blocked = false;
  let warned = false;

  for (const issue of issues) {
    const mode = issueApplies(issue, settings);
    if (mode === "always" || mode === "block") {
      blocked = true;
    } else if (mode === "warn") {
      warned = true;
    }
  }

  const actionableIssues = issues.filter((issue) => {
    const mode = issueApplies(issue, settings);
    return mode === "always" || mode === "block" || mode === "warn";
  });

  return {
    ok: actionableIssues.length === 0,
    blocked,
    warned: warned && !blocked,
    issues: actionableIssues,
    message:
      actionableIssues.length > 0
        ? buildMessage(
            actionableIssues,
            fileName,
            settings.listing_photo_min_short_edge_px,
          )
        : undefined,
    shortEdge: extras?.shortEdge,
    blurScore: extras?.blurScore,
    meanBrightness: extras?.meanBrightness,
  };
}

async function validateListingImageFileInner(
  file: File,
  settings: ListingPhotoCoachSettings,
): Promise<ListingImageValidationResult> {
  const issues: ListingImageValidationIssue[] = [];

  if (file.size > LISTING_IMAGE_MAX_BYTES) {
    issues.push("too_large");
  }

  if (
    !LISTING_IMAGE_ALLOWED_TYPES.includes(
      file.type as (typeof LISTING_IMAGE_ALLOWED_TYPES)[number],
    )
  ) {
    issues.push("unsupported_type");
  }

  const needsDimensionCheck =
    settings.listing_photo_min_dimension_mode !== "off";
  const needsQualityCheck = settings.listing_photo_quality_check_mode !== "off";

  if (!needsDimensionCheck && !needsQualityCheck) {
    return finalizeValidationResult(issues, settings, file.name);
  }

  if (issues.length > 0) {
    return finalizeValidationResult(issues, settings, file.name);
  }

  const decoded = await decodeListingImageOnce(file);
  if (!decoded) {
    return finalizeValidationResult(issues, settings, file.name, {
      shortEdge: undefined,
    });
  }

  const shortEdge = Math.min(decoded.width, decoded.height);

  if (
    needsDimensionCheck &&
    shortEdge > 0 &&
    shortEdge < settings.listing_photo_min_short_edge_px
  ) {
    issues.push("too_small");
  }

  let blurScore: number | undefined;
  let meanBrightness: number | undefined;

  if (needsQualityCheck && issues.length === 0 && decoded.analysisCanvas) {
    const quality = analyzeListingImageQualityFromCanvas(
      decoded.analysisCanvas,
    );
    blurScore = quality.blurScore;
    meanBrightness = quality.meanBrightness;

    if (blurScore < LISTING_IMAGE_BLUR_VARIANCE_THRESHOLD) {
      issues.push("too_blurry");
    }
    if (meanBrightness < LISTING_IMAGE_MIN_MEAN_BRIGHTNESS) {
      issues.push("too_dark");
    }
  }

  return finalizeValidationResult(issues, settings, file.name, {
    shortEdge: shortEdge > 0 ? shortEdge : undefined,
    blurScore,
    meanBrightness,
  });
}

/**
 * Validates a listing photo before upload (dimensions, blur, brightness).
 * Respects admin gate modes: block / warn / off.
 */
export async function validateListingImageFile(
  file: File,
  settings: ListingPhotoCoachSettings,
): Promise<ListingImageValidationResult> {
  const syncIssues: ListingImageValidationIssue[] = [];

  if (file.size > LISTING_IMAGE_MAX_BYTES) {
    syncIssues.push("too_large");
  }

  if (
    !LISTING_IMAGE_ALLOWED_TYPES.includes(
      file.type as (typeof LISTING_IMAGE_ALLOWED_TYPES)[number],
    )
  ) {
    syncIssues.push("unsupported_type");
  }

  if (syncIssues.length > 0) {
    return finalizeValidationResult(syncIssues, settings, file.name);
  }

  return withTimeout(
    validateListingImageFileInner(file, settings),
    LISTING_IMAGE_VALIDATION_TIMEOUT_MS,
    `${file.name}: Photo check took too long. Try a smaller image, close other tabs, or refresh and try again.`,
  );
}
